import { createAsyncThunk } from '@reduxjs/toolkit';
import instance from '../../axiosInstance';
import { deserializeData } from '../../utils/deserializer';
import { DealAiAgentRanking } from '../../interfaces/dealAiAgentRanking';

/**
 * Fetches the LLM-generated agent ranking for a deal.
 *
 * NOTE: takes the DEAL id (from `search.deal.id`), not the search id from the
 * `/deal/:dealId` URL param (which is actually a search id).
 *
 * The endpoint is a collection: `GET v4/deals/{dealId}/deal-ai-agent-rankings/` returns a
 * JSON:API list of rankings, newest first. We take the newest (first) entry as
 * the ranking. An empty list means no successful ranking exists for the deal
 * yet — a normal outcome that resolves to `null` (treated as control: no tabs).
 *
 * A 404 (the deal itself does not exist) collapses to that same `null` state.
 */
export const fetchAiRanking = createAsyncThunk<
  DealAiAgentRanking | null,
  string | number,
  { rejectValue: string }
>('aiRanking/fetchAiRanking', async (dealId, { rejectWithValue }) => {
  try {
    const response = await instance.get(
      `v4/deals/${encodeURIComponent(dealId)}/deal-ai-agent-rankings/`,
    );
    // The list is newest-first; take the newest, or null when empty.
    const rankings = deserializeData<DealAiAgentRanking[]>(response.data);
    return rankings?.[0] ?? null;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    return rejectWithValue(error?.message || 'Failed to fetch AI ranking');
  }
});
